/**
 * SPARC RFP RSS Feed Registry
 *
 * Merged from DefenseScope RSS_FEED_URL_CATALOG, M1 defense pack,
 * gov-contracting FeedSpot feeds, and SPARC-relevant verticals.
 *
 * Regenerate JSON: python TraceOS/scripts/rss/generate_sparc_rfp_feed_registry.py
 */

import registryData from '../data/sparcRfpFeedRegistry.json';

export interface RSSFeedSource {
  id: string;
  name: string;
  url: string;
  /** Publisher website (defaults to feed URL hostname root when omitted). */
  homepageUrl?: string;
  description: string;
  category: RSSFeedCategory;
  feedType: 'news' | 'opportunities';
  verified: boolean;
  updateFrequency: 'hourly' | 'daily' | 'weekly';
  enabled: boolean;
  priority: number; // 1-3, 1 being highest priority
  catalog_id?: string | null;
  health_status?: string;
  health_checked_at?: string;
  final_url?: string;
}

export type RSSFeedCategory =
  | 'government_contracts'
  | 'government_technology'
  | 'technology'
  | 'procurement_usa'
  | 'policy'
  | 'defense'
  | 'geopolitics'
  | 'commodity'
  | 'mining'
  | 'agriculture'
  | 'forestry'
  | 'market'
  | 'osint'
  | 'space';

/** Default alert keywords tuned for SPARC systems and GovEarner monitoring. */
export const SPARC_RFP_DEFAULT_KEYWORDS: readonly string[] = [
  'geospatial',
  'environmental consulting',
  'RFP',
  'procurement',
  'defense',
  'GIS',
  'remote sensing',
  'mining',
  'forestry',
  'government contracting',
  'AI',
  'satellite',
  'environmental assessment',
  'contaminated sites',
  'tender',
  'federal contract',
  'geospatial intelligence',
  'earth observation',
  'climate',
  'infrastructure',
];

/** High-value categories for default Industry News feed selection. */
export const SPARC_DEFAULT_FEED_CATEGORIES: readonly RSSFeedCategory[] = [
  'government_contracts',
  'government_technology',
  'procurement_usa',
  'policy',
  'defense',
  'geopolitics',
  'mining',
  'commodity',
  'osint',
  'technology',
  'space',
  'market',
  'agriculture',
  'forestry',
];

type RegistryFeed = Omit<RSSFeedSource, 'feedType'> & { feedType?: RSSFeedSource['feedType'] };

function normalizeFeed(feed: RegistryFeed): RSSFeedSource {
  return {
    ...feed,
    feedType: feed.feedType ?? 'news',
  };
}

export const RSS_FEED_REGISTRY: RSSFeedSource[] = (
  registryData as { feeds: RegistryFeed[] }
).feeds.map(normalizeFeed);

export function getFeedsByCategory(category: RSSFeedCategory): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter((feed) => feed.category === category && feed.enabled);
}

export function getEnabledFeeds(): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter((feed) => feed.enabled);
}

/** Feeds the user can toggle in Alert Settings (health-synced `enabled` flag). */
export function isFeedSelectable(feed: RSSFeedSource): boolean {
  return feed.enabled;
}

export const PRIORITY_LABELS: Record<number, string> = {
  1: 'High',
  2: 'Medium',
  3: 'Low',
};

export function getFeedsByPriority(priority: number): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter((feed) => feed.enabled && feed.priority === priority);
}

export function getTopPriorityFeeds(): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter((feed) => feed.enabled && feed.priority <= 2);
}

/** Default feeds for Alert Settings — enabled, verified, priority 1 plus SPARC-relevant priority 2 categories. */
export function getDefaultSelectedFeedIds(): string[] {
  return RSS_FEED_REGISTRY.filter(
    (feed) =>
      feed.enabled &&
      feed.verified &&
      (feed.priority === 1 ||
        (feed.priority <= 2 && SPARC_DEFAULT_FEED_CATEGORIES.includes(feed.category)))
  ).map((feed) => feed.id);
}

export function loadSelectedFeedIds(): string[] {
  if (typeof localStorage === 'undefined') {
    return getDefaultSelectedFeedIds();
  }

  const saved = localStorage.getItem('rfp-selected-feeds');
  if (!saved) {
    return getDefaultSelectedFeedIds();
  }

  try {
    const parsed = JSON.parse(saved) as string[];
    const filtered = parsed.filter((id) => {
      const feed = RSS_FEED_REGISTRY.find((entry) => entry.id === id);
      return feed !== undefined && feed.enabled;
    });
    return filtered.length > 0 ? filtered : getDefaultSelectedFeedIds();
  } catch {
    return getDefaultSelectedFeedIds();
  }
}

export function loadSelectedKeywords(): string[] {
  if (typeof localStorage === 'undefined') {
    return [...SPARC_RFP_DEFAULT_KEYWORDS];
  }

  const saved =
    localStorage.getItem('rfp-selected-keywords') ||
    localStorage.getItem('external-data-dashboard-keywords');

  if (!saved) {
    return [...SPARC_RFP_DEFAULT_KEYWORDS];
  }

  try {
    const parsed = JSON.parse(saved) as string[];
    return parsed.length > 0 ? parsed : [...SPARC_RFP_DEFAULT_KEYWORDS];
  } catch {
    return [...SPARC_RFP_DEFAULT_KEYWORDS];
  }
}

export function getFeedsByType(feedType: 'news' | 'opportunities'): RSSFeedSource[] {
  return RSS_FEED_REGISTRY.filter((feed) => feed.enabled && feed.feedType === feedType);
}

export function getNewsFeeds(): RSSFeedSource[] {
  return getFeedsByType('news');
}

export function getOpportunityFeeds(): RSSFeedSource[] {
  return getFeedsByType('opportunities');
}

export function getFeedById(id: string): RSSFeedSource | undefined {
  return RSS_FEED_REGISTRY.find((feed) => feed.id === id);
}

export function getAllCategories(): RSSFeedCategory[] {
  const categories = new Set<RSSFeedCategory>();
  RSS_FEED_REGISTRY.forEach((feed) => categories.add(feed.category));
  return Array.from(categories);
}

export const CATEGORY_NAMES: Record<RSSFeedCategory, string> = {
  government_contracts: 'Government Contracts',
  government_technology: 'Government Technology',
  technology: 'Technology & Innovation',
  procurement_usa: 'US Procurement',
  policy: 'Policy & Regulation',
  defense: 'Defense & Security',
  geopolitics: 'Geopolitics & Conflict',
  commodity: 'Commodity Markets',
  mining: 'Mining & Resources',
  agriculture: 'Agriculture',
  forestry: 'Forestry & Timber',
  market: 'Markets & Finance',
  osint: 'OSINT & Investigations',
  space: 'Space & Satellites',
};

export type FeedSortMode = 'priority' | 'volume' | 'name';

export interface RSSFeedVolumeStats {
  counts: Record<string, number>;
  updatedAt: string | null;
}

export const FEED_VOLUME_STATS_STORAGE_KEY = 'rfp-feed-volume-stats';
export const FEED_SORT_MODE_STORAGE_KEY = 'rfp-feed-sort-mode';

export function loadFeedVolumeStats(): RSSFeedVolumeStats {
  if (typeof localStorage === 'undefined') {
    return { counts: {}, updatedAt: null };
  }

  const saved = localStorage.getItem(FEED_VOLUME_STATS_STORAGE_KEY);
  if (!saved) {
    return { counts: {}, updatedAt: null };
  }

  try {
    const parsed = JSON.parse(saved) as RSSFeedVolumeStats;
    return {
      counts: parsed.counts ?? {},
      updatedAt: parsed.updatedAt ?? null,
    };
  } catch {
    return { counts: {}, updatedAt: null };
  }
}

export function saveFeedVolumeStats(counts: Record<string, number>): RSSFeedVolumeStats {
  const stats: RSSFeedVolumeStats = {
    counts,
    updatedAt: new Date().toISOString(),
  };

  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(FEED_VOLUME_STATS_STORAGE_KEY, JSON.stringify(stats));
  }

  return stats;
}

/** True when cached volume stats exist (timestamp or any per-feed counts). */
export function hasFeedVolumeStats(stats: RSSFeedVolumeStats): boolean {
  return stats.updatedAt !== null || Object.keys(stats.counts).length > 0;
}

/** Merge new per-feed counts into cached stats (preserves counts for feeds not in this fetch). */
export function mergeFeedVolumeCounts(
  incoming: Record<string, number>,
  existing: Record<string, number> = loadFeedVolumeStats().counts
): Record<string, number> {
  return { ...existing, ...incoming };
}

export function loadFeedSortMode(): FeedSortMode {
  if (typeof localStorage === 'undefined') {
    return 'priority';
  }

  const saved = localStorage.getItem(FEED_SORT_MODE_STORAGE_KEY);
  if (saved === 'volume' || saved === 'name' || saved === 'priority') {
    return saved;
  }

  return 'priority';
}

export function saveFeedSortMode(mode: FeedSortMode): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(FEED_SORT_MODE_STORAGE_KEY, mode);
  }
}

export function sortFeeds(
  feeds: RSSFeedSource[],
  mode: FeedSortMode,
  articleCountByFeedId: Record<string, number> = {}
): RSSFeedSource[] {
  const sorted = [...feeds];

  switch (mode) {
    case 'volume':
      return sorted.sort((a, b) => {
        const countA = articleCountByFeedId[a.id] ?? -1;
        const countB = articleCountByFeedId[b.id] ?? -1;
        if (countB !== countA) {
          return countB - countA;
        }
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        return a.name.localeCompare(b.name);
      });
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'priority':
    default:
      return sorted.sort((a, b) => {
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        return a.name.localeCompare(b.name);
      });
  }
}

export function sumFeedArticleCounts(
  feeds: RSSFeedSource[],
  articleCountByFeedId: Record<string, number>
): number {
  return feeds.reduce((sum, feed) => {
    if (!isFeedSelectable(feed)) {
      return sum;
    }
    return sum + (articleCountByFeedId[feed.id] ?? 0);
  }, 0);
}

/** Badge label for feed row; dead feeds show em dash, unknown counts omitted. */
export function formatFeedArticleCount(
  feed: RSSFeedSource,
  articleCountByFeedId: Record<string, number>
): string | null {
  if (!isFeedSelectable(feed)) {
    return '—';
  }

  const count = articleCountByFeedId[feed.id];
  if (count === undefined) {
    return null;
  }

  return count === 1 ? '1 article' : `${count} articles`;
}
